import fetch from 'helpers/command'
import {orderBy} from 'external/lodash'
import {isSameOrAfter, isSameOrBefore, getValue} from 'external/date-fns'

export function getGroupGrades(items = []) {
    const groupGrades = items.reduce((students, grade) => {
      if (!students[grade.student])
        students[grade.student] = {}
      const student = students[grade.student]
      const id = grade.makeupFor || grade.assignment || grade.grade
      const grades = student[id] || (student[id] = []) 
      grades.push(grade)
      return students
    }, {})
    return (studentId, columnId) => (groupGrades[studentId] && groupGrades[studentId][columnId]) || []
}

export function getGroupMembers(groupId, groups = []) {
    const group = groups.find(group => group.id === groupId) || {}
    return (group && group.students) || []
}

export function filterGradeColumnsByPeriod(columns, period) {
    return columns.filter(column => {
        if (!period) return true
        const isSameOrAfterPeriod = isSameOrAfter(column.start, period.start)
        const isSameOrBeforePeriod = isSameOrBefore(column.start, period.finish)
        return isSameOrAfterPeriod && isSameOrBeforePeriod
    })
}

export function gradeMatchesPeriod(column) {
    return (period) => {
        const isSameOrAfterPeriod = isSameOrAfter(column.start, period.start)
        const isSameOrBeforePeriod = isSameOrBefore(column.start, period.finish)
        return isSameOrAfterPeriod && isSameOrBeforePeriod
    }
}

export function filterStudentsByPattern (students = [], pattern) {
    if (!pattern) return students
    const filter = ({name}) => name.normalize().toLowerCase().indexOf(pattern.normalize().toLowerCase()) >= 0
    return students.filter(filter)
}

export function getStudentGrades(students, gradeColumns, findStudentGrades, assignmentProperties) {
   return students.map(student => {
       const name = student.name
       const columns = gradeColumns.map(column => {
           const grades = findStudentGrades(student.id, column.id)
           const {properties} = (assignmentProperties[student.id] || {})[column.id] || {properties: {ignore: column.external ? false : true}}
           const ignore = column.properties.ignore || properties.ignore
           const assigned = column.external || !!(assignmentProperties[student.id] && assignmentProperties[student.id][column.id])
           const parseColumn = column.external
              ? parseExternalGrade
              : column.type == 'homework'
                  ? parseHomeworkGrade
                  : parseAssessmentGrade
           const parsed = parseColumn({...column, ignore, assigned}, grades)
           return parsed
       })
       const columnSorting = columns.reduce((sorting, column) => {
           const isExternal = !!column.external
           const isHomework = column.type == 'homework'
           const isCorrectedAssessment = column.answered && !column.pending
           sorting[column.label] = isExternal || isHomework || isCorrectedAssessment ? column.points : 0
           return sorting
       }, {})
       const points = columns.filter(c => !c.ignore).reduce((points, column) => points + column.points, 0) 
       const total = columns.filter(c => !c.ignore).reduce((total, column) => total + column.total, 0)
       const summary = (total == 0 ? 0 : (points / total * 100)).toFixed(2)
       return {...student, name, summary, points, total, columns, ...columnSorting}
   })
}

function parseAssessmentGrade(column, grades) {
    const answered = grades.filter(g => g.answeredAssessment != null)
    let selectedAnswer = answered.find(a => a.properties && a.properties.selected) 
    if (!selectedAnswer)
        selectedAnswer = answered.find(a => a.makeupFor == null)
    const points = selectedAnswer ? selectedAnswer.points + (selectedAnswer.properties.bonus || 0) : 0
    return {...column, selectedAnswer, answered: answered.length > 0, pending: selectedAnswer && selectedAnswer.pending, points, grades}
}

function parseExternalGrade(column, grades) {
   const [grade] = grades
   const pending = grades.length <= 0
   const notGraded = pending || grade.value === null
   const points = grade ? grade.value : null
   return {...column, grades, pending, notGraded, points}
}

function parseHomeworkGrade(column, grades) {
    const [grade = {}] = grades
    const pending = grades.length <= 0
    const notGraded = pending || grade.notGraded
    const points = grade.points || 0
    return {...column, grades, pending, notGraded, points}
}

const sorters = {
  student: (items, order) => orderBy(items, ['name'], [order]),
  summary: (items, order) => orderBy(items, ['summary'], [order]),
  grade: (items, order, orderColumn) => orderBy(items, [orderColumn], [order]),
}

export function sortStudentGrades({order, orderColumn}, studentGrades) {
    const sorter = sorters[orderColumn] || (orderColumn ? sorters.grade : sorters.student)
    return sorter(studentGrades, order, orderColumn)
}

export function getSelectedPeriod(periods, periodId) {
      return periods.length == 0
        ? undefined
        : (periods.find(periodId
              ? p => p.id == periodId
              : p => isSameOrAfter(new Date(), p.start) && isSameOrBefore(new Date(), p.finish)
            ) || periods[0]
          )
}


export async function getAllGrades({translate, gradingCategoryMap, quizTypesMap, homeworkStatus}) {

    const data = await fetch('grading', 'getAllGrades')()

    const grades = data.filter(i => i.recordType == 'grade')
    const gradeColumns = data.filter(i => i.recordType == 'column')
    const assignmentProperties = data.filter(i => i.recordType == 'properties')

    const processedGrades = grades.map(grade => {
      if (grade.type != 'homework') return grade
      const [reviewed = {}] = orderBy((grade.log || []).filter(e => homeworkStatus[e.status] == 'completed'), e => getValue(e.created), ['desc'])
      const [turned = {}] = orderBy((grade.log || []).filter(e => homeworkStatus[e.status] == 'pending'), e => getValue(e.created), ['desc'])
      return {...grade, finished: turned.created, points: reviewed.score || 0, notGraded: reviewed.score == undefined}
    })
    const typeCounts = {}
    const columnLabel = (groupId, type) => {
      const groupCounts = typeCounts[groupId]
        ? typeCounts[groupId] 
        : typeCounts[groupId] = {}
      groupCounts[type] = groupCounts[type]
        ? groupCounts[type] + 1
        : 1
      return  `${type}${groupCounts[type]}`
    }
    const sortedByDate = orderBy(gradeColumns, [p => getValue(p.start)])
    const columns = sortedByDate.map(column => {
      const type = column.type == 'homework'
        ? 'homework'
        : column.external
          ? gradingCategoryMap[column.type]
          : quizTypesMap[column.type]
      return {...column, type, label: columnLabel(column.group,translate(`${type}-label`)[0])}
    })

    const properties = assignmentProperties.reduce((acc, {id, assignment, student, properties}) => {
      const students = acc[student] || (acc[student] = {})
      students[assignment] = {id, properties}
      return acc
    }, {})

    return {grades: processedGrades, gradeColumns: columns, properties}

}

export function displayPoints(grade) {
  const raw = (grade.points || 0) + (grade.properties?.bonus || 0)
  return raw % 1 === 0 ? raw : parseFloat(raw.toFixed(2))
}